/**
 * Utilidades para validar tokens JWT
 */

import { jwtDecode } from 'jwt-decode';

interface DecodedToken {
  role: 'candidato' | 'reclutador' | 'admin';
  uid: string;
  exp?: number;
  [key: string]: unknown;
}

/**
 * Verifica si un token JWT está expirado
 * @param token - Token JWT a verificar
 * @returns true si el token está expirado o es inválido, false si es válido
 */
export function isTokenExpired(token: string): boolean {
  try {
    const decoded = jwtDecode(token) as DecodedToken;
    
    // Si no tiene campo exp, considerar como no expirado (puede ser un token sin expiración)
    if (!decoded.exp) {
      return false;
    }
    
    // Comparar con el tiempo actual (en segundos)
    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
  } catch {
    // Si hay error decodificando, considerar como expirado/inválido
    return true;
  }
}

/**
 * Verifica si un token es válido (no expirado y decodificable)
 * @param token - Token JWT a verificar
 * @returns true si el token es válido, false si está expirado o es inválido
 */
export function isTokenValid(token: string | null): boolean {
  if (!token || token.trim().length === 0) {
    return false;
  }
  
  return !isTokenExpired(token);
}

/**
 * Obtiene el rol del usuario desde el token
 * @param token - Token JWT
 * @returns El rol del usuario o null si no se puede decodificar
 */
export function getRoleFromToken(token: string): 'candidato' | 'reclutador' | 'admin' | null {
  try {
    const decoded = jwtDecode(token) as DecodedToken;
    const role = decoded.role;
    
    if (!role) {
      return null;
    }
    
    // Normalizar el rol (por si viene en mayúsculas o con espacios)
    const normalizedRole = String(role).toLowerCase().trim();
    // Aceptar reclutador/candidato o "recruiter" como reclutador
    if (normalizedRole === 'reclutador' || normalizedRole === 'recruiter' || normalizedRole.includes('reclutador')) {
      return 'reclutador';
    }
    if (normalizedRole === 'candidato') return 'candidato';
    if (normalizedRole === 'admin') return 'admin';
    return null;
  } catch {
    return null;
  }
}

/**
 * Lo único que sobrevive a cerrar sesión: preferencias del dispositivo, no datos
 * de nadie.
 *
 * Es una lista de lo que SE QUEDA, no de lo que se borra. Al revés —ir borrando
 * claves conocidas— cualquier clave nueva que alguien añada sobrevive al logout
 * por omisión, que es justo como el progreso de una postulación acabó
 * reapareciendo en la sesión siguiente.
 */
const CLAVES_DE_DISPOSITIVO = new Set([
  'app_theme',
  'app_lang',
  'recruiter_sidebar_collapsed',
]);

/**
 * Borra todo rastro de la sesión anterior en este navegador.
 *
 * Antes sólo se quitaba `user_token`, así que quedaban el progreso de la
 * postulación (`application_data_*`, `stepper_*`, `application_completed_*`),
 * la conexión de Calendar y las notificaciones. Al volver a entrar, la
 * plataforma reanudaba una candidatura que el usuario no había elegido —incluso
 * siendo otra persona en el mismo equipo—.
 */
export function clearSessionData(): void {
  if (typeof localStorage === 'undefined') return;
  const aBorrar: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && !CLAVES_DE_DISPOSITIVO.has(key)) {
      aBorrar.push(key);
    }
  }
  aBorrar.forEach((key) => localStorage.removeItem(key));
}

export function clearAuthToken(redirectToLogin: boolean = true): void {
  if (typeof localStorage !== 'undefined') {
    clearSessionData();

    if (redirectToLogin && typeof window !== 'undefined') {
      window.location.href = '/';
    }
  }
}

/**
 * Identificador con el que se guardan los datos de una sesión, derivado del
 * correo del token. Debe coincidir con el que usa ApplicationStepper.
 */
export function getUserIdentifierFromToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const decoded = jwtDecode(token) as DecodedToken;
    const email = typeof decoded.email === 'string' ? decoded.email : null;
    return email ? email.replace(/[^a-zA-Z0-9]/g, '_') : null;
  } catch {
    return null;
  }
}
